import { useCallback, ReactElement, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Typography, styled, Box, Stack } from '@mui/material';
import { AuthService, useApiEffect, isDarkMode, formatError, useSnackbar } from '@localstack/services';
import { ProgressButton } from '@localstack/ui';
import { Cancel as CancelIcon } from '@mui/icons-material';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { BaseLayout } from '~/layouts';

import { NoNavbarHeader } from '../PricingPlans/components';

const Container = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  height: 'calc(100% - 140px)',
  textAlign: 'center',
}));

const WarningContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: isDarkMode(theme) ? theme.palette.common.black : theme.palette.common.white,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.divider}`,
  textAlign: 'left',
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
}));

export const AzureMarketplaceActivation = (): ReactElement => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const { reloadUserInfo } = useAuthProvider();
  // Azure Marketplace appends the token as a query param (?token=...) on the
  // registered landing page URL, unlike AWS which uses a path param.
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { showSnackbar } = useSnackbar();

  const {
    exchangeAzureMarketplaceToken,
    isLoading: isExchangingToken,
    error,
    hasError,
  } = useApiEffect(AuthService, ['exchangeAzureMarketplaceToken'], { suppressErrors: true });

  const warningMessage = error ? formatError(error) : undefined;

  const subscribe = useCallback(async () => {
    const response = await exchangeAzureMarketplaceToken({ token });
    if (response) {
      showSnackbar({ severity: 'success', message: 'Subscription was activated successfully' });
      await reloadUserInfo(); // pick up new adjusted permissions
      navigate(AppRoute.GETTING_STARTED);
      return;
    }

    setShowWarning(true);
    if (hasError) {
      showSnackbar({ severity: 'error', message: 'Failed to activate Subscription' });
    }
  }, [token, hasError]);

  return (
    <BaseLayout hideNavigation>
      <NoNavbarHeader />
      <Container>
        <Box maxWidth={340}>
          <Typography variant="h3" component="h2" fontWeight={600} mb={2}>
            Activate Azure subscription
          </Typography>
          <Typography fontSize={16} mb={4}>
            Once you activate your subscription, all payments and subscription management will be handled through your
            Azure account.
          </Typography>
          {showWarning && (
            <WarningContainer role="alert" aria-live="polite">
              <CancelIcon color="error" sx={{ marginRight: '8px' }} />
              <Stack>
                <Typography fontWeight={600} sx={{ width: '100%', lineHeight: '24px' }}>
                  Failed to activate your subscription.
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{warningMessage}</Typography>
              </Stack>
            </WarningContainer>
          )}
          <ProgressButton
            color="primary"
            variant="contained"
            disableElevation
            loading={isExchangingToken}
            onClick={subscribe}
            fullWidth
          >
            Activate Subscription
          </ProgressButton>
        </Box>
      </Container>
    </BaseLayout>
  );
};
